// PostgreSQL sequence-reference slot — `nextval('▮')`, `currval('▮')`,
// `setval('▮', …)`.
//
// PG has no `NEXT VALUE FOR` statement: a sequence is named by a STRING
// LITERAL passed to one of three functions, so the caret that wants a
// sequence name sits inside an unclosed `'…` literal. `deriveContext` flags
// that as `insideStringOrComment` and the resolver bails, which is why the
// slot answered with nothing but Monaco's word-scrape (pgsql script.45
// §5/§7/§8). Like the enum-value and `DATE_TRUNC('▮'` collectors, this one
// therefore runs BEFORE the string bail and owns its own Monaco edit range.
//
// The literal is `regclass` text, so it takes the same shapes a relation
// reference does: bare (`nextval('film_film_id_seq')`, resolved through
// `search_path`) or schema-qualified (`nextval('public.film_film_id_seq')`).
// Both are recognised, and the emitted candidates match the shape the caret
// is already in — bare names after a `public.` qualifier, qualified names at
// a bare caret whose sequence is not on the search_path.

import type { CompletionCandidate, SequenceMeta } from '@/core/engines';
import { scanBufferCreatedSequences } from '../context/isAtNextValueForSlot';
import { formatIdentifier, formatQualifiedTwoPart } from './pgsqlIdentifiers';

/** The three PG functions whose FIRST argument is a sequence name. */
export type PgSequenceFunction = 'nextval' | 'currval' | 'setval';

const PG_SEQUENCE_FUNCTIONS: ReadonlySet<string> = new Set([
  'nextval',
  'currval',
  'setval',
]);

export type PgSequenceLiteralSlot = {
  readonly fn: PgSequenceFunction;
  /**
   * The schema typed before the dot inside the literal (`nextval('public.▮`),
   * unquoted and verbatim. `null` for a bare caret (`nextval('▮`), where the
   * search_path decides.
   */
  readonly qualifier: string | null;
  /** The in-progress name after the qualifier (possibly empty). */
  readonly partial: string;
  /** Absolute offset where {@link partial} starts — the replace range's start. */
  readonly partialStart: number;
};

/**
 * True when `body` (the literal's text so far) is still a plausible
 * `[schema.]sequence` reference. A space, comma or paren OUTSIDE double
 * quotes means the caret has left the identifier and is inside prose the user
 * is inventing — better to fall through to the normal string bail than to
 * offer a sequence there. Inside `"…"` those characters are part of a
 * legitimately quoted identifier (`nextval('"My Schema".s')`), so quoting is
 * tracked rather than banned.
 */
function isPartialSequenceRef(body: string): boolean {
  let inQuote = false;
  for (const ch of body) {
    if (ch === '"') {
      inQuote = !inQuote;
      continue;
    }
    if (inQuote) continue;
    if (/[\s,()]/.test(ch)) return false;
  }
  return true;
}

/**
 * Index of the qualifier/name separating dot — the LAST dot that sits outside
 * double quotes, so a dot inside a quoted identifier (`"a.b"`) is not read as
 * a namespace separator. Returns -1 for an unqualified body.
 */
function lastUnquotedDot(body: string): number {
  let inQuote = false;
  let found = -1;
  for (let i = 0; i < body.length; i += 1) {
    const ch = body[i];
    if (ch === '"') {
      inQuote = !inQuote;
      continue;
    }
    if (!inQuote && ch === '.') found = i;
  }
  return found;
}

/** Strip one layer of PG double quoting from an identifier segment. */
function unquote(raw: string): string {
  return raw.startsWith('"') && raw.endsWith('"') && raw.length >= 2
    ? raw.slice(1, -1).replace(/""/g, '"')
    : raw;
}

/**
 * Recognise a caret inside the sequence-name literal of `nextval` /
 * `currval` / `setval`, or return null.
 *
 * Same walk as `findPgDateFieldLiteralSlot` (the other closed-vocabulary
 * string slot): find the literal's opening quote, require the literal to be
 * the callee's FIRST argument (`(` immediately in front of it), then read the
 * callee name and reject a schema-qualified call — `myschema.nextval(` is a
 * user routine that happens to share the name.
 */
export function findPgSequenceLiteralSlot(
  source: string,
  statementStart: number,
  cursor: number,
): PgSequenceLiteralSlot | null {
  let i = cursor - 1;
  let quoteStart = -1;
  while (i >= statementStart) {
    const ch = source[i];
    if (ch === '\n') return null;
    if (ch === "'") {
      // A doubled `''` is an escaped quote inside the literal, not its start.
      if (source[i - 1] === "'") {
        i -= 2;
        continue;
      }
      quoteStart = i;
      break;
    }
    i -= 1;
  }
  if (quoteStart < 0) return null;

  // The literal must be the FIRST argument: `(` directly precedes the quote.
  let j = quoteStart - 1;
  while (j >= statementStart && /\s/.test(source[j]!)) j -= 1;
  if (j < statementStart || source[j] !== '(') return null;

  // Read the callee name.
  let k = j - 1;
  while (k >= statementStart && /\s/.test(source[k]!)) k -= 1;
  const nameEnd = k + 1;
  while (k >= statementStart && /[A-Za-z0-9_]/.test(source[k]!)) k -= 1;
  const name = source.slice(k + 1, nameEnd).toLowerCase();
  if (source[k] === '.') return null; // qualified → a user routine
  if (!PG_SEQUENCE_FUNCTIONS.has(name)) return null;

  const body = source.slice(quoteStart + 1, cursor);
  if (!isPartialSequenceRef(body)) return null;

  const dot = lastUnquotedDot(body);
  const partial = dot >= 0 ? body.slice(dot + 1) : body;
  const qualifierRaw = dot >= 0 ? body.slice(0, dot) : null;
  return {
    fn: name as PgSequenceFunction,
    qualifier: qualifierRaw === null ? null : unquote(qualifierRaw),
    partial,
    partialStart: cursor - partial.length,
  };
}

type CollectedSequence = {
  readonly name: string;
  readonly schema: string | null;
  readonly fromBuffer: boolean;
};

/**
 * Snapshot sequences merged with the ones this buffer's own `CREATE SEQUENCE`
 * declares. Script 45 references `public.acact_invoice_seq` three statements
 * after creating it, and the snapshot cannot know a sequence whose DDL has
 * not executed yet — so a buffer-declared sequence leads (tier 1) and
 * snapshot ones follow (tier 2). Mirrors the MSSQL `collectSequences` policy.
 */
function collectPgSequences(
  snapshot: readonly SequenceMeta[],
  text: string,
): CollectedSequence[] {
  const seen = new Set<string>();
  const out: CollectedSequence[] = [];
  for (const b of scanBufferCreatedSequences(text)) {
    const key = `${(b.schema ?? '').toLowerCase()}|${b.name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: b.name, schema: b.schema, fromBuffer: true });
  }
  for (const s of snapshot) {
    const key = `${(s.schema ?? '').toLowerCase()}|${s.name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: s.name, schema: s.schema ?? null, fromBuffer: false });
  }
  return out;
}

/**
 * The sequence candidates for a recognised {@link PgSequenceLiteralSlot}.
 *
 * The caret is INSIDE the quotes the buffer already carries, so every
 * `insertText` is bare (never re-quoted) — the same contract
 * `emitJsonPathCandidates` and the enum-value emitter honour.
 *
 * Shape mirrors the caret:
 *   - after a qualifier (`nextval('public.▮`) → that schema's sequences, bare
 *     names, because the qualifier is already typed;
 *   - at a bare caret (`nextval('▮`) → search_path sequences bare (that is
 *     what a PG user writes), everything else `schema.name`.
 */
export function emitPgSequenceCandidates(
  slot: PgSequenceLiteralSlot,
  sequences: readonly SequenceMeta[],
  text: string,
  searchPath: readonly string[],
): CompletionCandidate[] {
  const out: CompletionCandidate[] = [];
  const qualifierFolded = slot.qualifier?.toLowerCase() ?? null;
  const pathFolded = new Set(searchPath.map((s) => s.toLowerCase()));

  for (const s of collectPgSequences(sequences, text)) {
    const schemaFolded = (s.schema ?? '').toLowerCase();
    if (qualifierFolded !== null) {
      if (schemaFolded !== qualifierFolded) continue;
      out.push({
        label: s.name,
        insertText: formatIdentifier(s.name),
        isSnippet: false,
        kind: 'system-object',
        tier: s.fromBuffer ? 1 : 2,
        detail: s.fromBuffer ? 'Sequence (this script)' : 'Sequence',
        filterText: s.name,
      });
      continue;
    }
    const bare = !s.schema || pathFolded.has(schemaFolded);
    const label = bare ? s.name : `${s.schema}.${s.name}`;
    out.push({
      label,
      insertText: bare
        ? formatIdentifier(s.name)
        : formatQualifiedTwoPart(s.schema!, s.name),
      isSnippet: false,
      kind: 'system-object',
      tier: s.fromBuffer ? 1 : 2,
      detail: s.fromBuffer ? 'Sequence (this script)' : 'Sequence',
      filterText: label,
    });
  }
  return out;
}
